import React, { useState } from 'react';
import { convertDateTimeString } from '@/date';
import { toCurrencyString } from '@/number';
import BaseTable from '@/components/EntityModal/Table/BaseTable';
import Icon from '@/components/Icon';
import DealTabEmpty from '@/components/EntityModal/DealTabEmpty';
import EntityLink from '@/components/EntityLink';
import Truncate from '@/components/Truncate';
import { FullDealStatusSelect } from '@/components/Inputs';
import {
  useUpdateDeal
} from '@/api';
import APIErrorMessage from '@/components/APIErrorMessage';
import { useAlert } from 'react-alert';
import RequiredFieldsModal, { useRequiredFieldsModal } from '@/components/RequiredFieldsModal';

const tableConfigForEntity = ({
  entityType, update, alert,
  checkRequiredFields, setShow
}) => {
  const updateDeal = (params, dealId) => {
    update.mutate({ dealId, params }, {
      onError: (err) => {
        alert.show(
          <APIErrorMessage err={err} resource='deal' action='update' />,
          { variant: 'danger', timeout: 5000 }
        );
      },
      onSuccess: () => {
        alert.show(
          'Negócio atualizado com sucesso!',
          { variant: 'success', timeout: 5000 }
        );
      }
    });
  };

  const handleStatusChange = async (deal, selectedStatus) => {
    const funnel = deal.stage.funnel;


    if (funnel.hasRequiredFields) {
      const fields = {
        deal_status_id: selectedStatus.deal_status_id,
        loss_reason_id: selectedStatus.deal_loss_reason?.loss_reason_id,
        funnel_id: funnel.id
      };

      if (selectedStatus.deal_loss_reason?.description) {
        fields.loss_reason_description = selectedStatus.deal_loss_reason.description;
      }

      const dealHasRequiredFields = await checkRequiredFields(deal, fields);

      if (dealHasRequiredFields) {
        setShow(true);
        return;
      }
    }

    return updateDeal({ ...selectedStatus }, deal.id);
  };

  const headers = [
    'Nome',
    'FUNIL',
    ...(entityType === 'organization' ? ['CONTATO'] : []),
    'STATUS',
    'ETAPA',
    'VALOR',
    'DATA DE INÍCIO',
    'DATA DE CONCLUSÃO',
    'ÚLTIMA ATUALIZAÇÃO',
    'DATA DE CADASTRO'
  ];

  const columns = [
    {
      callback: (value) => value,
      objectPath: 'stage.funnel.name'
    },
    ...(entityType === 'organization'
      ? [
        {
          callback: (item) => {
            const entity = item.organization || item.person;
            const resolvedEntityType = item.organization ? 'organization' : 'person';
            return (
              <>
                <div className='d-flex'>
                  <Icon name={resolvedEntityType} size='sm' className='me-2' />
                  <EntityLink entity={{ id: entity.id, type: resolvedEntityType }}>
                    <Truncate as='div' className='max-width-5'>
                      {entity.name}
                    </Truncate>
                  </EntityLink>
                </div>
              </>
            );
          },
          objectPath: ''
        }
      ]
      : []),
    {
      callback: (deal) => (
        <div className='width-5'>
          <FullDealStatusSelect
            onChange={(selectedStatus) => handleStatusChange(deal, selectedStatus)}
            value={deal?.dealStatus.id}
          />
        </div>
      )
    },
    {
      callback: (value) => value,
      objectPath: 'stage.name'
    },
    {
      callback: (value) => toCurrencyString(value),
      objectPath: 'value'
    },
    {
      callback: (value) => convertDateTimeString(value),
      objectPath: 'startedAt'
    },
    {
      callback: (value) => convertDateTimeString(value),
      objectPath: 'finishedAt'
    },
    {
      callback: (value) => convertDateTimeString(value),
      objectPath: 'updatedAt'
    },
    {
      callback: (value) => convertDateTimeString(value),
      objectPath: 'createdAt'
    }
  ];

  return { headers, columns };
};

function DealTabTable({
  fetchNextPage,
  hasNextPage,
  isLoading,
  entityId,
  items,
  itemsTotal,
  relatedEntityType
}) {
  const [showRequiredFieldsModal, setShowRequiredFieldsModal] = useState(false);
  const update = useUpdateDeal();
  const alert = useAlert();

  const {
    check: checkRequiredFields,
    ...requiredFieldsModal
  } = useRequiredFieldsModal();

  const { headers, columns } = tableConfigForEntity({
    entityType: relatedEntityType,
    update,
    alert,
    checkRequiredFields,
    requiredFieldsModal,
    setShow: setShowRequiredFieldsModal
  });

  const handleCancel = () => {
    setShowRequiredFieldsModal(false);
  };

  return (
    <>
      <BaseTable
        entityType='deal'
        headers={headers}
        columns={columns}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isLoading={isLoading}
        entityId={entityId}
        items={items}
        itemsTotal={itemsTotal}
        relatedEntityType={relatedEntityType}
        EmptyComponent={<DealTabEmpty entityType={relatedEntityType} entityId={entityId} />}
      />

      <RequiredFieldsModal
        {...requiredFieldsModal}
        onHide={handleCancel}
        show={showRequiredFieldsModal}
        onFinish={() => {
          requiredFieldsModal.onHide();
          setShowRequiredFieldsModal(false);
        }}
      />
    </>
  );
}

export default DealTabTable;
